import { useMemo, useCallback, useState } from 'react';

/**
 * Hook to prepare props objects for MyCloset's large child components.
 * This reduces the JSX prop spreading in MyCloset.js significantly.
 */
const useMyClosetProps = ({
    // State
    state,
    // Layout
    layout,
    // Modals
    modals,
    // Panels
    panels,
    // Actions
    actions,
    // Positioning
    positioning,
    // Virtual Try-On
    virtualTryOn,
    // Drag and Drop
    dragDrop,
    // Canvas Interactions
    canvasInteractions,
    // Bulk Selection
    bulkSelection,
    // Background
    backgroundState,
    // Person Image
    personImageState,
    activePersonImage,
    hasPersonImage,
    // Clothing Items
    actualClothingItems,
    // Credits
    creditBalance,
    // Other
    isItemSelectedForTryOn,
    canSelectForTryOn,
    selectedClosetId,
    guest,
    isLoggedIn,
    lookbookPanel,
    onOpenEmailModal,
    onOpenExtensionModal,
    onOpenUrlImportModal,
    isDropUploading
}) => {

    // State for scroll-to-item (cleared after use)
    const [scrollToItemId, setScrollToItemId] = useState(null);

    // VTO Bottom Panel Props
    const vtoBottomPanelProps = useMemo(() => ({
        virtualTryOnResults: state.virtualTryOnResults,
        onPersonImageUploadModalOpen: panels.handleVTOWearerClick,
        droppedItems: state.droppedItems,
        isVirtualMode: state.isVirtualMode,
        hasPersonImage,
        canvasRef: state.placeholderRef,
        panelHeight: layout.panelHeight,
        panelPosition: layout.panelPosition,
        isCollapsed: layout.isCollapsed,
        externalCollapsed: panels.isVTOPanelCollapsed,
        onExternalCollapseChange: panels.setIsVTOPanelCollapsed,
        onRemoveItem: virtualTryOn.handleRemoveItem,
        disabled: state.isProcessing,
        canvasWidth: state.placeholderWidth,
        canvasHeight: state.placeholderHeight,
        background: backgroundState.background,
        activePersonImage,
        // Open clothing panel (expand panel and close any lookbook view)
        onOpenClothingPanel: () => {
            // Switch from lookbook to clothing panel view
            if (lookbookPanel?.panelViewType === 'lookbook') {
                lookbookPanel.setPanelViewType('closet');
            }
            // Close panel view (folder view) if open
            if (lookbookPanel?.showPanelView) {
                lookbookPanel.setShowPanelView(false);
            }
            // Expand panel if collapsed
            if (layout.isCollapsed && layout.handleCollapseToggle) {
                layout.handleCollapseToggle(false);
            }
        },
        // Scroll to a specific item in the clothing panel
        onScrollToItem: (item) => {
            // Switch from lookbook to clothing panel view
            if (lookbookPanel?.panelViewType === 'lookbook') {
                lookbookPanel.setPanelViewType('closet');
            }
            // Close panel view (folder view) if open
            if (lookbookPanel?.showPanelView) {
                lookbookPanel.setShowPanelView(false);
            }
            // Expand panel if collapsed
            if (layout.isCollapsed && layout.handleCollapseToggle) {
                layout.handleCollapseToggle(false);
            }
            // Set the scroll target — ClothingCardsGrid will handle expanding visibleCount and scrolling
            setScrollToItemId(item.id);
            // Clear after a delay so it can be re-triggered for the same item
            setTimeout(() => setScrollToItemId(null), 2000);
        }
    }), [
        state.virtualTryOnResults, panels.handleVTOWearerClick, state.droppedItems,
        state.isVirtualMode, hasPersonImage, state.placeholderRef, layout.panelHeight,
        layout.panelPosition, layout.isCollapsed, panels.isVTOPanelCollapsed,
        panels.setIsVTOPanelCollapsed, virtualTryOn.handleRemoveItem, state.isProcessing,
        state.placeholderWidth, state.placeholderHeight, backgroundState.background, activePersonImage,
        lookbookPanel, layout.handleCollapseToggle, actualClothingItems.length
    ]);

    // Add hasClothingItems to vtoBottomPanelProps
    const vtoBottomPanelPropsWithItems = useMemo(() => ({
        ...vtoBottomPanelProps,
        hasClothingItems: actualClothingItems.length > 0
    }), [vtoBottomPanelProps, actualClothingItems.length]);

    // Manual Control Panel Props
    const manualControlPanelProps = useMemo(() => ({
        isVisible: !state.isVirtualMode,
        panelHeight: layout.panelHeight,
        panelPosition: layout.panelPosition,
        isPanelCollapsed: layout.isCollapsed,
        externalCollapsed: panels.isManualPanelCollapsed,
        onExternalCollapseChange: panels.setIsManualPanelCollapsed,
        canvasWidth: state.placeholderWidth,
        showGrid: state.showGrid,
        showSnapGuides: state.showSnapGuides,
        gridSize: state.gridSize,
        onToggleGrid: positioning.handleToggleGrid,
        onToggleSnapGuides: positioning.handleToggleSnapGuides,
        onGridSizeChange: positioning.handleGridSizeChange,
        onAlignLeft: actions.handleAlignLeft,
        onAlignCenter: actions.handleAlignCenter,
        onAlignRight: actions.handleAlignRight,
        onAlignTop: actions.handleAlignTop,
        onAlignMiddle: actions.handleAlignMiddle,
        onAlignBottom: actions.handleAlignBottom,
        onDistributeHorizontally: actions.handleDistributeHorizontally,
        onDistributeVertically: actions.handleDistributeVertically,
        selectedItemsCount: state.multiSelectedItems.length,
        droppedItems: state.droppedItems,
        selectedItem: state.selectedItem,
        onDeleteSelection: actions.handleDeleteSelection,
        onDeleteAll: actions.handleDeleteAll,
        onCreateLook: actions.handleCreateLook,
        onSaveLook: actions.saveLook,
        isLookPopupOpen: state.isLookPopupOpen,
        setIsLookPopupOpen: state.setIsLookPopupOpen,
        isNoItemsPopupOpen: state.isNoItemsPopupOpen,
        lookName: state.lookName,
        setLookName: state.setLookName,
        canUndo: state.canUndo,
        canRedo: state.canRedo,
        onUndo: state.undoAction,
        onRedo: state.redoAction,
        historyInfo: state.getHistoryInfo()
    }), [
        state.isVirtualMode, layout.panelHeight, layout.panelPosition, layout.isCollapsed,
        panels.isManualPanelCollapsed, panels.setIsManualPanelCollapsed, state.placeholderWidth,
        state.showGrid, state.showSnapGuides, state.gridSize, positioning, actions,
        state.multiSelectedItems.length, state.droppedItems, state.selectedItem,
        state.isLookPopupOpen, state.setIsLookPopupOpen, state.isNoItemsPopupOpen,
        state.lookName, state.setLookName, state.canUndo, state.canRedo,
        state.undoAction, state.redoAction, state.getHistoryInfo
    ]);

    // Unified Canvas Controls Props
    const unifiedCanvasControlsProps = useMemo(() => ({
        onHomeClick: actions.handleHomeClick,
        onFolderClick: actions.handleFolderClick,
        onPersonImageUploadModalOpen: panels.handlePersonImageModalOpen,
        showBackgroundCustomizer: false,
        setShowBackgroundCustomizer: () => { },
        background: backgroundState.background,
        onBackgroundChange: backgroundState.setBackground,
        onBackgroundImageUpload: backgroundState.uploadBackground,
        onBackgroundModalOpen: panels.handleBackgroundModalOpen,
        showPositioningControls: state.showPositioningControls,
        setShowPositioningControls: state.setShowPositioningControls,
        showGrid: state.showGrid,
        showSnapGuides: state.showSnapGuides,
        gridSize: state.gridSize,
        onToggleGrid: positioning.handleToggleGrid,
        onToggleSnapGuides: positioning.handleToggleSnapGuides,
        onGridSizeChange: positioning.handleGridSizeChange,
        onAlignLeft: actions.handleAlignLeft,
        onAlignCenter: actions.handleAlignCenter,
        onAlignRight: actions.handleAlignRight,
        onAlignTop: actions.handleAlignTop,
        onAlignMiddle: actions.handleAlignMiddle,
        onAlignBottom: actions.handleAlignBottom,
        onDistributeHorizontally: actions.handleDistributeHorizontally,
        onDistributeVertically: actions.handleDistributeVertically,
        selectedItemsCount: state.multiSelectedItems.length,
        canUndo: state.canUndo,
        canRedo: state.canRedo,
        onUndo: state.undoAction,
        onRedo: state.redoAction,
        historyInfo: state.getHistoryInfo(),
        droppedItems: state.droppedItems,
        isLookPopupOpen: state.isLookPopupOpen,
        setIsLookPopupOpen: state.setIsLookPopupOpen,
        isNoItemsPopupOpen: state.isNoItemsPopupOpen,
        lookName: state.lookName,
        setLookName: state.setLookName,
        onCreateLook: actions.handleCreateLook,
        onSaveLook: actions.saveLook,
        selectedItem: state.selectedItem,
        onDeleteSelection: actions.handleDeleteSelection,
        onDeleteAll: actions.handleDeleteAll,
        virtualTryOnResults: state.virtualTryOnResults,
        availableWidth: state.placeholderWidth,
        isCollapsed: panels.isControlsCollapsed,
        onToggleCollapse: panels.handleToggleControlsCollapse,
        isSuccessPopupOpen: state.isSuccessPopupOpen,
        setIsSuccessPopupOpen: state.setIsSuccessPopupOpen,
        folderIconRef: state.folderIconRef
    }), [
        actions, panels, backgroundState, state, positioning
    ]);

    // MyCloset Canvas Props
    const myClosetCanvasProps = useMemo(() => ({
        placeholderRef: state.placeholderRef,
        placeholderWidth: state.placeholderWidth,
        placeholderHeight: state.placeholderHeight,
        background: backgroundState.background,
        onDragOver: dragDrop.handleDragOver,
        onDragLeave: dragDrop.handleDragLeave,
        onDrop: dragDrop.handleDrop,
        onPlaceholderClick: canvasInteractions.handlePlaceholderClick,
        panelPosition: layout.panelPosition,
        showGrid: state.showGrid,
        gridSize: state.gridSize,
        showSnapGuides: state.showSnapGuides,
        isMoving: state.isMoving,
        snapLines: state.snapLines,
        isVirtualMode: state.isVirtualMode,
        toggleMode: state.toggleMode,
        onModeChange: actions.handleModeChange,
        isTryOnLoading: state.isTryOnLoading,
        tryOnError: state.tryOnError,
        clearTryOnError: state.clearTryOnError,
        droppedItems: state.droppedItems,
        selectedItem: state.selectedItem,
        multiSelectedItems: state.multiSelectedItems,
        onItemClick: canvasInteractions.handleItemClick,
        onMoveStart: canvasInteractions.handleMoveStart,
        onMoveEnd: canvasInteractions.handleMoveEnd,
        onResizeStart: canvasInteractions.handleResizeStart,
        onResizeEnd: canvasInteractions.handleResizeEnd,
        onRotateStart: canvasInteractions.handleRotateStart,
        onRotateEnd: canvasInteractions.handleRotateEnd,
        onItemMove: canvasInteractions.handleItemMove,
        onItemResize: canvasInteractions.handleItemResize,
        onItemRotate: canvasInteractions.handleItemRotate,
        onRevertToOriginal: virtualTryOn.handleRevertToOriginal,
        onRetryVirtualTryOn: virtualTryOn.handleRetryVirtualTryOn,
        onPersonImageModalOpen: panels.handlePersonImageModalOpen,
        onBackgroundModalOpen: panels.handleBackgroundModalOpen,
        onLoginRequired: panels.handleLoginRequired,
        // Toolbar — uses direct handlers (skip VTO collapse)
        onDirectPersonModalOpen: panels.handleDirectPersonModalOpen,
        onDirectBackgroundModalOpen: panels.handleDirectBackgroundModalOpen,
        // Toolbar panel toggles
        onClothingPanelToggle: () => {
            const isShowingClothing = lookbookPanel?.panelViewType === 'closet' || (lookbookPanel?.panelViewType !== 'lookbook' && lookbookPanel?.panelViewType !== 'explore');
            const isPanelOpen = !layout.isCollapsed;

            if (isShowingClothing && isPanelOpen) {
                // Already showing clothing and open → collapse
                if (layout.handleCollapseToggle) {
                    layout.handleCollapseToggle(true);
                }
            } else {
                // Switch to clothing view
                if (lookbookPanel?.panelViewType === 'lookbook' || lookbookPanel?.panelViewType === 'explore') {
                    lookbookPanel.setPanelViewType('closet');
                }
                if (lookbookPanel?.showPanelView) {
                    lookbookPanel.setShowPanelView(false);
                }
                // Expand if collapsed
                if (layout.isCollapsed && layout.handleCollapseToggle) {
                    layout.handleCollapseToggle(false);
                }
            }
        },
        onLookbookToggle: () => {
            const isShowingLookbook = lookbookPanel?.panelViewType === 'lookbook';
            const isPanelOpen = !layout.isCollapsed;

            if (isShowingLookbook && isPanelOpen) {
                // Already showing lookbook and open -> collapse
                if (layout.handleCollapseToggle) {
                    layout.handleCollapseToggle(true);
                }
            } else {
                // Switch to lookbook
                if (!isShowingLookbook) {
                    lookbookPanel?.handleOpenLookbookPanel?.();
                }
                // Expand if collapsed
                if (layout.isCollapsed && layout.handleCollapseToggle) {
                    layout.handleCollapseToggle(false);
                }
            }
        },
        onCommunityToggle: () => {
            const isShowingCommunity = lookbookPanel?.panelViewType === 'explore';
            const isPanelOpen = !layout.isCollapsed;

            if (isShowingCommunity && isPanelOpen) {
                if (layout.handleCollapseToggle) {
                    layout.handleCollapseToggle(true);
                }
            } else {
                lookbookPanel?.setPanelViewType?.('explore');
                lookbookPanel?.setShowPanelView?.(true);
                if (layout.isCollapsed && layout.handleCollapseToggle) {
                    layout.handleCollapseToggle(false);
                }
            }
        },
        onEarningsToggle: () => {
            modals.setIsEarningsModalOpen(true);
        },
        isClothingPanelActive: lookbookPanel?.panelViewType !== 'lookbook' && lookbookPanel?.panelViewType !== 'explore' && !layout.isCollapsed,
        isLookbookActive: lookbookPanel?.panelViewType === 'lookbook',
        isCommunityActive: lookbookPanel?.panelViewType === 'explore',
        isEarningsActive: modals.isEarningsModalOpen,
        isPersonModalOpen: modals.isPersonImageModalOpen,
        isBackgroundModalOpen: modals.isBackgroundModalOpen,
        canUndo: state.canUndo,
        canRedo: state.canRedo,
        onUndo: state.undoAction,
        onRedo: state.redoAction,
        getHistoryInfo: state.getHistoryInfo,
        isPersonImageUploading: isDropUploading
    }), [
        state, backgroundState.background, dragDrop, canvasInteractions,
        layout.panelPosition, actions.handleModeChange, virtualTryOn, panels,
        isDropUploading
    ]);

    // Clothing Items Panel Props (shared base)
    // The toolbar's search button toggles the panel. Entry points inside the
    // closet ("explore more", the import modal) must only ever open it —
    // toggling there would close the panel the user just asked to see.
    const openExplorePanel = useCallback(() => {
        lookbookPanel?.setPanelViewType?.('explore');
        lookbookPanel?.setShowPanelView?.(true);
        if (layout.isCollapsed && layout.handleCollapseToggle) {
            layout.handleCollapseToggle(false);
        }
    }, [lookbookPanel, layout]);

    const clothingItemsPanelBaseProps = useMemo(() => ({
        guest,
        isBulkSelectMode: state.isBulkSelectMode,
        selectedItems: state.selectedItems,
        onItemSelect: bulkSelection.handleItemSelect,
        onAddToCloset: bulkSelection.handleAddToCloset,
        onBulkDelete: bulkSelection.handleBulkDelete,
        onDragStart: dragDrop.handleDragStart,
        onDragEnd: dragDrop.handleDragEnd,
        isItemSelectedForTryOn,
        canSelectForTryOn,
        disabled: state.isProcessing,
        selectedClosetId,
        hasClothingItems: actualClothingItems.length > 0,
        isVirtualMode: state.isVirtualMode,
        onModeChange: actions.handleModeChange,
        hasPersonImage,
        isVirtualTryOnAvailable: state.isAvailable,
        getStatusMessage: state.getStatusMessage,
        onOpenClothingModal: modals.handleOpenClothingModal,
        onOpenEmailModal,
        onOpenExtensionModal,
        onOpenUrlImportModal,
        onLoginRequired: panels.handleLoginRequired,
        onExploreClick: openExplorePanel,
        scrollToItemId
    }), [
        guest, state, bulkSelection, dragDrop, isItemSelectedForTryOn, canSelectForTryOn,
        selectedClosetId, actualClothingItems.length, actions.handleModeChange, hasPersonImage, modals.handleOpenClothingModal, panels.handleLoginRequired, openExplorePanel, scrollToItemId
    ]);

    // Create bulk edit click handler
    const handleBulkEditClick = useCallback(() => {
        state.setIsBulkSelectMode(!state.isBulkSelectMode);
        if (state.isBulkSelectMode) state.setSelectedItems([]);
    }, [state.isBulkSelectMode, state.setIsBulkSelectMode, state.setSelectedItems]);

    // Processing Modal Props
    const processingModalProps = useMemo(() => ({
        isOpen: modals.isProcessingModalOpen,
        progress: modals.processingProgress,
        message: modals.processingMessage,
        creditBalance,
        creditsUsed: modals.processingCreditsUsed,
        isCached: modals.processingIsCached
    }), [modals, creditBalance]);

    // Error Modal Props
    const errorModalProps = useMemo(() => ({
        isOpen: modals.isErrorModalOpen,
        error: modals.currentError,
        onClose: modals.hideErrorModal,
        onRetry: modals.currentRetryAction ? modals.handleErrorRetry : null,
        onSwitchToManual: modals.currentManualAction ? modals.handleErrorSwitchToManual : null,
        showRetry: !!modals.currentRetryAction,
        showSwitchToManual: !!modals.currentManualAction
    }), [modals]);

    // General Error Modal Props
    const generalErrorModalProps = useMemo(() => ({
        isOpen: modals.isGeneralErrorModalOpen,
        onClose: modals.hideGeneralErrorModal,
        title: modals.generalErrorConfig.title,
        message: modals.generalErrorConfig.message,
        type: modals.generalErrorConfig.type,
        icon: modals.generalErrorConfig.icon,
        iconColor: modals.generalErrorConfig.iconColor
    }), [modals]);

    // MyCloset Modals Props
    const myClosetModalsProps = useMemo(() => ({
        isPersonImageModalOpen: modals.isPersonImageModalOpen,
        onPersonImageModalClose: modals.handlePersonImageModalClose,
        currentPersonImageUrl: activePersonImage?.image?.url || null,
        isBackgroundModalOpen: modals.isBackgroundModalOpen,
        onBackgroundModalClose: modals.handleBackgroundModalClose,
        currentBackground: backgroundState.background,
        onBackgroundChange: backgroundState.setBackground,
        onBackgroundUpload: backgroundState.uploadBackground,
        isLoginModalOpen: modals.isLoginModalOpen,
        onLoginModalClose: modals.handleLoginModalClose,
        onLoginRedirect: modals.handleLoginRedirect,
        isVTOLoginModalOpen: modals.isVTOLoginModalOpen,
        onVTOLogin: modals.handleVTOLogin,
        hideVTOLoginModal: modals.hideVTOLoginModal,
        isBulkDeleteModalOpen: modals.isBulkDeleteModalOpen,
        setBulkDeleteModalOpen: modals.setIsBulkDeleteModalOpen,
        selectedItems: state.selectedItems,
        onBulkDeleteSuccess: bulkSelection.handleBulkDeleteSuccess,
        isAddToClosetModalOpen: modals.isAddToClosetModalOpen,
        setAddToClosetModalOpen: modals.setIsAddToClosetModalOpen,
        isClothingModalOpen: modals.isClothingModalOpen,
        editClothingItemId: modals.editClothingItemId,
        onCloseClothingModal: modals.handleCloseClothingModal
    }), [modals, activePersonImage, backgroundState, state.selectedItems, bulkSelection.handleBulkDeleteSuccess]);


    // Lookbook Items Panel Props
    const lookbookItemsPanelProps = useMemo(() => ({
        // Pass necessary props similar to LookbookItemsPanel construction in LookBook.js
        lookbookId: lookbookPanel.selectedLookbookId,
        panelPosition: layout.panelPosition,
        panelHeight: layout.panelHeight,
        // Reusing bulk selection mode from state - or should we use separate state?
        // Using same bulk select mode for simplicity for now, but usually they are distinct.
        // MyCloset.js uses state.isBulkSelectMode shared across panels currently?
        // Actually clothingItemsPanelBaseProps uses state.isBulkSelectMode.
        // Let's assume we want lookbook items to be selectable too.
        isBulkSelectMode: state.isBulkSelectMode,
        selectedItems: state.selectedItems,
        onItemSelect: bulkSelection.handleItemSelect, // This might need adaptation for Lookbook entries if IDs conflict
        onBulkEditClick: handleBulkEditClick,
        onCardClick: lookbookPanel.handleLookbookEntrySelect,
        onDeleteClick: (id) => {/* Handle single delete if needed */ },
        // We might need specific handlers for lookbook entries if they differ from clothing items
    }), [
        lookbookPanel.selectedLookbookId, layout.panelPosition, layout.panelHeight,
        state.isBulkSelectMode, state.selectedItems, bulkSelection, handleBulkEditClick, lookbookPanel
    ]);

    // Resizable Layout Props
    const resizableLayoutProps = useMemo(() => ({
        panelPosition: layout.panelPosition,
        panelWidth: layout.panelWidth,
        panelHeight: layout.panelHeight,
        isCollapsed: layout.isCollapsed,
        onResize: layout.handleResize,
        onPositionChange: layout.handlePositionChange,
        onCollapseToggle: layout.handleCollapseToggle,
        isLoggedIn,
        minPanelWidth: 280,
        maxPanelWidth: 600,
        minPanelHeight: 150,
        maxPanelHeight: 400,
        isBulkSelectMode: state.isBulkSelectMode,
        hasClothingItems: actualClothingItems.length > 0,
        selectedCount: state.selectedItems.length,
        totalCount: actualClothingItems.length,
        onSelectAll: bulkSelection.handleSelectAll,
        onUnselectAll: bulkSelection.handleUnselectAll,
        showPanelView: lookbookPanel.showPanelView,
        onPanelViewToggle: lookbookPanel.setShowPanelView,
        closetPanelState: lookbookPanel.closetPanelState,
        lookbookPanelState: lookbookPanel.lookbookPanelState,
        panelViewType: lookbookPanel.panelViewType,
        selectedClosetName: lookbookPanel.panelViewType === 'lookbook' ? lookbookPanel.selectedLookbookName : lookbookPanel.selectedClosetName,
        onBackToClosets: lookbookPanel.panelViewType === 'lookbook' ? lookbookPanel.handleBackToLookbooks : lookbookPanel.handleBackToClosets,
        isVirtualMode: state.isVirtualMode,
        onModeChange: actions.handleModeChange,
        hasPersonImage,
        onBulkEditClick: handleBulkEditClick,
        onOpenEmailModal,
        onOpenExtensionModal,
        onOpenUrlImportModal,
    }), [
        layout, isLoggedIn, state.isBulkSelectMode, actualClothingItems.length,
        state.selectedItems.length, bulkSelection, lookbookPanel,
        state.isVirtualMode, actions.handleModeChange, hasPersonImage, handleBulkEditClick
    ]);

    return {
        vtoBottomPanelProps: vtoBottomPanelPropsWithItems,
        manualControlPanelProps,
        unifiedCanvasControlsProps,
        myClosetCanvasProps,
        clothingItemsPanelBaseProps,
        lookbookItemsPanelProps,
        handleBulkEditClick,
        processingModalProps,
        errorModalProps,
        generalErrorModalProps,
        myClosetModalsProps,
        resizableLayoutProps
    };
};

export default useMyClosetProps;
